// frontend/src/services/courses.service.ts
//
// Okuma işlemleri (kurs/ders listeleme) doğrudan Supabase'e gider — RLS
// zaten "giriş yapan herkese açık" politikasıyla korunuyor, bu yüzden
// backend'i bu basit GET'ler için araya sokmaya gerek yok.
// Yazma işlemleri (ders tamamlama, XP ekleme gibi yan etkisi olanlar)
// backend'deki /api/courses/* route'larına gider çünkü atomik bir
// veritabanı fonksiyonu (complete_lesson) çalıştırılması gerekiyor.
//
// PERFORMANS OPTİMİZASYONLARI:
// - queryCache ile istek de-duplikasyonu ve TTL önbelleği
// - Backend artık lessonCount döndüğü için ayrı lessons sorgusu kaldırıldı
// - select('*') yerine sadece gerekli alanlar çekiliyor

import { supabase } from '../lib/supabaseClient';
import { authService } from './auth.service';
import { Course, Lesson, LessonTopic, CompleteLessonResponse, UnderstandingFeedback } from '../types/courses.types';
import { queryCache, CACHE_TTL } from '../lib/queryCache';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

/** Kurs listesinde kullanılan Supabase alanları */
const COURSE_SELECT_FIELDS = 'id, title, description, category, difficulty, cover_image_url, order_index, age_category';

/** Temel ders alanları - her DB şemasında kesin bulunan alanlar */
const BASE_LESSON_FIELDS = 'id, title, course_id, order_index, xp_reward, image_url, is_premium, is_preview, video_url, created_at';

/** Ders listesinde kullanılan genişletilmiş Supabase alanları */
const LESSON_LIST_FIELDS = `${BASE_LESSON_FIELDS}, duration_minutes, description`;

export const coursesService = {
  async listCourses(): Promise<Course[]> {
    return queryCache.dedupe('courses:list', async () => {
      try {
        const token = await authService.getAccessToken();
        const res = await fetch(`${API_BASE}/api/courses`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          return data.courses ?? [];
        }
      } catch (e) {
        console.warn('[coursesService.listCourses] API fallback fallbacking to Supabase:', e);
      }

      const { data, error } = await supabase
        .from('courses')
        .select(COURSE_SELECT_FIELDS)
        .order('order_index', { ascending: true });

      if (error) throw error;
      return data ?? [];
    }, CACHE_TTL.COURSES);
  },

  /**
   * Giriş yapmış öğrencinin tüm kurslardaki ilerlemesini, kurs listesiyle birleştirir.
   *
   * OPTİMİZASYON:
   * - Backend /api/courses artık lessonCount döndüğü için
   *   ayrı bir "tüm lessons tablosunu çek" sorgusu kaldırıldı.
   * - Sadece 2 paralel sorgu: kurslar + öğrenci progress
   */
  async listCoursesWithProgress(studentId: string): Promise<Course[]> {
    const [courses, progressRes] = await Promise.all([
      this.listCourses(),
      supabase
        .from('student_course_progress')
        .select('course_id, status, progress_percent')
        .eq('student_id', studentId),
    ]);

    if (progressRes.error) throw progressRes.error;

    const progressByCourse = new Map(
      (progressRes.data ?? []).map((p) => [p.course_id, p])
    );

    // lessonCount artık API'den geliyor; gelmemişse hesaplama yapılmaz (0 gösterilir)
    return (courses ?? []).map((course) => {
      const progress = progressByCourse.get(course.id);
      return {
        ...course,
        lessonCount: course.lessonCount ?? 0,
        progressPercent: progress?.progress_percent ?? 0,
        status: progress?.status ?? 'not_started',
      };
    });
  },

  async getCourse(courseId: string): Promise<Course | null> {
    return queryCache.dedupe(`courses:detail:${courseId}`, async () => {
      try {
        const token = await authService.getAccessToken();
        const res = await fetch(`${API_BASE}/api/courses/${courseId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          return data.course ?? null;
        }
      } catch (e) {
        console.warn('[coursesService.getCourse] API fallback to Supabase:', e);
      }

      const { data, error } = await supabase
        .from('courses')
        .select(COURSE_SELECT_FIELDS)
        .eq('id', courseId)
        .single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return data;
    }, CACHE_TTL.COURSE_DETAIL);
  },

  async listLessons(courseId: string): Promise<Lesson[]> {
    return queryCache.dedupe(`lessons:list:${courseId}`, async () => {
      try {
        const token = await authService.getAccessToken();
        const res = await fetch(`${API_BASE}/api/courses/${courseId}/lessons`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          return data.lessons ?? [];
        }
      } catch (e) {
        console.warn('[coursesService.listLessons] API fallback to Supabase:', e);
      }

      // Fallback: sadece listeleme alanlarını çek (content hariç)
      let { data, error } = await supabase
        .from('lessons')
        .select(LESSON_LIST_FIELDS)
        .eq('course_id', courseId)
        .order('order_index', { ascending: true });

      // DB'de duration_minutes veya description sütunu henüz yoksa (42703), temel alanlarla tekrar dene
      if (error && (error.code === '42703' || error.message?.includes('duration_minutes') || error.message?.includes('description'))) {
        console.warn('[coursesService.listLessons] duration_minutes/description sütunu DB\'de henüz yok, base alanlarla çekiliyor...');
        const fallbackRes = await supabase
          .from('lessons')
          .select(BASE_LESSON_FIELDS)
          .eq('course_id', courseId)
          .order('order_index', { ascending: true });
        data = fallbackRes.data;
        error = fallbackRes.error;
      }

      if (error) throw error;
      return (data as unknown as Lesson[]) ?? [];
    }, CACHE_TTL.LESSONS_LIST);
  },

  async getLesson(lessonId: string): Promise<Lesson | null> {
    const { data, error } = await supabase.from('lessons').select('*').eq('id', lessonId).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async listLessonTopics(lessonId: string): Promise<LessonTopic[]> {
    try {
      const token = await authService.getAccessToken();
      const res = await fetch(`${API_BASE}/api/courses/lessons/${lessonId}/topics`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        return data.topics ?? [];
      }
    } catch (e) {
      console.warn('[coursesService.listLessonTopics] API fallback to Supabase:', e);
    }

    const { data, error } = await supabase
      .from('lesson_topics')
      .select('*')
      .eq('lesson_id', lessonId)
      .order('order_index', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },


  /**
   * Dersi tamamlandı olarak işaretler. Backend'deki atomik fonksiyonu tetikler
   * (XP ekleme + ilerleme güncelleme tek transaction'da).
   */
  async completeLesson(
    lessonId: string,
    understanding?: UnderstandingFeedback
  ): Promise<CompleteLessonResponse> {
    const token = await authService.getAccessToken();
    if (!token) throw new Error('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/courses/lessons/${lessonId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ understanding }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.error?.message || 'Ders tamamlanamadı.');
    }

    // Ders tamamlandıktan sonra kurs önbelleğini temizle (ilerleme değişti)
    queryCache.invalidate('courses:');

    return data;
  },

  async getNotes(): Promise<any[]> {
    const token = await authService.getAccessToken();
    if (!token) throw new Error('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/courses/notes`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.error?.message || 'Notlar alınamadı.');
    }
    return data.notes ?? [];
  },

  async saveNote(payload: { courseId: string; lessonId: string; pdfNote?: string; userNote?: string }): Promise<any> {
    const token = await authService.getAccessToken();
    if (!token) throw new Error('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/courses/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.error?.message || 'Not kaydedilemedi.');
    }
    return data.note;
  },

  async deleteNote(noteId: string): Promise<void> {
    const token = await authService.getAccessToken();
    if (!token) throw new Error('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/courses/notes/${noteId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.error?.message || 'Not silinemedi.');
    }
  }
};
